import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { SiteNav } from './SiteNav';
import { 
  ArrowRight, 
  ArrowLeft,
  CheckCircle2, 
  FileText, 
  Users, 
  Send, 
  Eye, 
  MessageSquare, 
  BarChart3, 
  UserCheck, 
  FileSignature, 
  Home,
  Shield
} from 'lucide-react';

const phases = [
  {
    key: 'prepare',
    title: 'Prepare the tender pack',
    icon: FileText,
    plain:
      'You provide the minimum information and we build a professional Schedule of Works so every contractor prices the same scope.',
    outputs: ['Scaled drawings', 'Photos', 'Schedule of Works', 'Tender return form', 'Covering letter', 'Workmanship specification']
  },
  {
    key: 'shortlist',
    title: 'Shortlist suitable contractors',
    icon: Users,
    plain:
      'Pick contractors that match your project size and type. Invite a small number so you get quality attention.',
    outputs: ['3 to 5 contractors', 'Insurance and accreditations check', 'Relevant experience examples']
  },
  {
    key: 'issue',
    title: 'Issue the same information to everyone',
    icon: Send,
    plain:
      'Send the same pack, same return date, same questions. This creates like for like pricing and reduces exclusions.',
    outputs: ['Return deadline', 'Access arrangements', 'Pricing rules', 'How to list exclusions']
  },
  {
    key: 'sitevisit',
    title: 'One viewing window',
    icon: Eye,
    plain:
      'Arrange one site visit window for all bidders where possible. This reduces delays and keeps assumptions consistent.',
    outputs: ['Site visit date', 'Q and A deadline', 'Access constraints']
  },
  {
    key: 'clarify',
    title: 'Clarifications and addendum',
    icon: MessageSquare,
    plain:
      'If anything changes, you issue one addendum to all bidders. Nobody gets different information.',
    outputs: ['Tender addendum', 'Updated drawings if needed', 'Updated schedule if needed']
  },
  {
    key: 'compare',
    title: 'Compare returns properly',
    icon: BarChart3,
    plain:
      'Use the tender return form to compare breakdowns, exclusions, programme, and payment terms. Do not compare lump sums only.',
    outputs: ['Comparison table', 'Exclusions list', 'Programme durations', 'Cashflow and payment terms']
  },
  {
    key: 'appoint',
    title: 'Appoint the preferred contractor',
    icon: UserCheck,
    plain:
      'Select on value, not just lowest price. Confirm who runs the job day to day and lock assumptions down.',
    outputs: ['Selection rationale', 'Start date', 'Named site lead', 'Final scope confirmation']
  },
  {
    key: 'contract',
    title: 'Contract and mobilisation',
    icon: FileSignature,
    plain:
      'Put the agreement in writing before work starts. This protects you and the contractor.',
    outputs: ['Contract terms', 'Payment schedule', 'Programme', 'Insurances', 'H and S arrangements']
  },
  {
    key: 'handover',
    title: 'Deliver, snag, handover',
    icon: Home,
    plain:
      'Track progress against the schedule, record changes, and close out with snagging and certificates.',
    outputs: ['Snag list', 'Certificates', 'O and M info if required', 'Completion sign off']
  }
];

const FlowChart = ({ active, onSelect }) => {
  return (
    <div className="space-y-3 mt-6">
      {phases.map((phase, index) => {
        const Icon = phase.icon;
        const isActive = phase.key === active;
        const isCompleted = phases.findIndex(p => p.key === active) > index;
        
        return (
          <div key={phase.key}>
            <button
              onClick={() => onSelect(phase.key)}
              className={`w-full flex items-center gap-3 p-4 rounded-lg border transition-all text-left ${
                isActive 
                  ? 'border-primary-navy bg-bg-soft' 
                  : isCompleted 
                  ? 'border-primary-navy/30 bg-bg-soft' 
                  : 'border-border-light hover:border-primary-navy/30'
              }`}
            >
              <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                isActive 
                  ? 'bg-primary-navy text-white' 
                  : isCompleted 
                  ? 'bg-primary-navy text-white' 
                  : 'bg-border-light text-text-muted'
              }`}>
                {isCompleted ? (
                  <CheckCircle2 className="w-5 h-5" />
                ) : (
                  <Icon className="w-5 h-5" />
                )}
              </div>
              <div className="flex-1">
                <div className={`font-semibold text-sm ${isActive ? 'text-primary-navy' : 'text-text-primary'}`}>
                  {phase.title}
                </div>
              </div>
              {isActive && <ArrowRight className="w-5 h-5 text-primary-navy" />}
            </button>
            {index < phases.length - 1 && (
              <div className={`w-0.5 h-3 mx-9 ${isCompleted ? 'bg-primary-navy' : 'bg-border-light'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
};

const DiagramCards = () => {
  const comparisons = [
    { title: 'Clear scope', body: 'Everyone prices the same items. Fewer exclusions and fewer misunderstandings.', icon: CheckCircle2 },
    { title: 'Comparable prices', body: 'Returns come back in a consistent format, so you can compare properly.', icon: BarChart3 },
    { title: 'Less risk loading', body: 'Clear scope reduces unknowns, so less contingency gets added.', icon: Shield },
    { title: 'Lender confidence', body: 'A professional quote pack supports funding conversations with evidence of credible build costs.', icon: FileSignature },
    { title: 'Investor ready', body: 'Looks organised and investable. Helps with JV discussions and deal packaging.', icon: Users },
    { title: 'Faster decisions', body: 'Cleaner returns reduce time spent chasing clarifications and missing items.', icon: ArrowRight }
  ];

  return (
    <div className="grid grid-cols-2 gap-4 mt-6">
      {comparisons.map((item, index) => {
        const Icon = item.icon;
        return (
          <div key={phase.title || index} className="card-styled p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-bg-soft rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon className="w-5 h-5 text-primary-navy" />
              </div>
              <div>
                <div className="font-semibold text-text-primary mb-1 text-sm">{item.title}</div>
                <div className="text-sm text-text-muted leading-relaxed">{item.body}</div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const TenderProcessPage = () => {
  const navigate = useNavigate();
  const [active, setActive] = useState('prepare');

  const activePhase = useMemo(() => phases.find((p) => p.key === active), [active]);

  return (
    <div className="min-h-screen bg-bg-soft">
      {/* Navigation */}
      <SiteNav cta="estimate" />

      {/* Main Content */}
      <main className="pt-24 pb-20">
        <div className="content-container">
          {/* Page Header */}
          <div className="text-center mb-12">
            <p className="section-label text-primary-navy mb-3">TENDER PROCESS</p>
            <h1 className="text-primary-navy mb-4">
              A simple step by step tender journey
            </h1>
            <p className="text-lg text-text-muted max-w-3xl mx-auto leading-relaxed">
              Clear scope reduces risk. Reduced risk usually reduces price. A professional tender pack also improves lender and investor confidence because it shows you have credible numbers, a controlled process, and evidence of market tested pricing.
            </p>
          </div>

          {/* Hero Grid */}
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <div className="card-styled">
              <h2 className="text-2xl font-semibold text-primary-navy mb-4">At a glance</h2>
              <p className="text-text-muted mb-4">
                The goal is simple. Every contractor prices the same scope, returns in the same format, and is compared on the same criteria.
              </p>
              <FlowChart active={active} onSelect={setActive} />
            </div>

            <div className="card-styled">
              <h2 className="text-2xl font-semibold text-primary-navy mb-4">
                What improves when your pack is professional
              </h2>
              <DiagramCards />
            </div>
          </div>

          {/* Detail Section */}
          <div className="grid md:grid-cols-4 gap-8">
            {/* Sidebar Navigation */}
            <div className="md:col-span-1">
              <div className="sticky top-24">
                <h3 className="font-semibold text-text-primary mb-4">Quick Navigation</h3>
                <div className="space-y-1">
                  {phases.map((phase) => (
                    <button
                      key={phase.key}
                      onClick={() => setActive(phase.key)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                        phase.key === active
                          ? 'bg-primary-navy text-white'
                          : 'text-text-muted hover:bg-bg-soft hover:text-text-primary'
                      }`}
                    >
                      {phase.title}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="md:col-span-3">
              <div className="card-styled border-2 border-primary-navy">
                {activePhase && (
                  <div className="space-y-6">
                    <div className="flex items-center gap-4">
                      {React.createElement(activePhase.icon, {
                        className: 'w-10 h-10 text-primary-navy'
                      })}
                      <h2 className="text-2xl font-semibold text-primary-navy">
                        {activePhase.title}
                      </h2>
                    </div>

                    <p className="text-text-muted leading-relaxed">
                      {activePhase.plain}
                    </p>

                    <div className="border-t border-border-light pt-6">
                      <h3 className="text-lg font-semibold text-text-primary mb-4">
                        Key Outputs
                      </h3>
                      <ul className="space-y-3">
                        {activePhase.outputs.map((output, index) => (
                          <li key={item || index} className="flex items-start gap-3">
                            <CheckCircle2 className="w-5 h-5 text-primary-navy flex-shrink-0 mt-0.5" />
                            <span className="text-text-muted">{output}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Navigation Buttons */}
                    <div className="flex justify-between pt-6 border-t border-border-light">
                      <button
                        onClick={() => {
                          const currentIndex = phases.findIndex(p => p.key === active);
                          if (currentIndex > 0) {
                            setActive(phases[currentIndex - 1].key);
                          }
                        }}
                        className="btn-secondary flex items-center gap-2"
                        disabled={phases.findIndex(p => p.key === active) === 0}
                      >
                        <ArrowLeft className="w-4 h-4" />
                        Previous Step
                      </button>
                      <button
                        onClick={() => {
                          const currentIndex = phases.findIndex(p => p.key === active);
                          if (currentIndex < phases.length - 1) {
                            setActive(phases[currentIndex + 1].key);
                          }
                        }}
                        className="btn-primary flex items-center gap-2"
                        disabled={phases.findIndex(p => p.key === active) === phases.length - 1}
                      >
                        Next Step
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* CTA Section */}
              <div className="card-styled mt-8 text-center bg-bg-soft border-0">
                <h3 className="text-xl font-semibold text-primary-navy mb-3">
                  Ready to start your tender process?
                </h3>
                <p className="text-text-muted mb-6 max-w-2xl mx-auto">
                  Get your professional Schedule of Works prepared and start receiving accurate, comparable quotes from contractors.
                </p>
                <button
                  onClick={() => navigate('/upload')}
                  className="btn-primary px-8 py-3"
                >
                  Get Your Schedule of Works
                </button>
              </div>
            </div>
          </div>

          {/* Lender and Investor Confidence Section */}
          <div className="grid md:grid-cols-2 gap-8 mt-16">
            <div className="card-styled">
              <h2 className="text-xl font-semibold text-primary-navy mb-4">
                Why this helps with lenders and investors
              </h2>
              <ul className="space-y-3">
                {[
                  'Shows you have market tested numbers, not a guess',
                  'Demonstrates you can control scope, risk, and procurement',
                  'Supports drawdown conversations when you can evidence a credible build cost',
                  'Improves deal packaging for JV partners and purchasers by making the scope transparent'
                ].map((item, index) => (
                  <li key={item || index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary-navy flex-shrink-0 mt-0.5" />
                    <span className="text-text-muted">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 p-4 bg-bg-soft border border-border-light rounded-lg">
                <p className="text-sm text-text-muted leading-relaxed">
                  A professional quote pack makes you look organised and investable. It reduces unanswered questions around cost, scope, and delivery risk.
                </p>
              </div>
            </div>

            <div className="card-styled">
              <h2 className="text-xl font-semibold text-primary-navy mb-4">
                <a href="https://www.macventure.co.uk" target="_blank" rel="noopener noreferrer" className="hover:underline">
                  MacVenture Consultancy Limited
                </a> support services
              </h2>
              <p className="text-text-muted mb-4">
                If you want more support beyond the tender pack, you can add these services at any time.
              </p>
              <ul className="space-y-3">
                {[
                  'Contract preparation support and contract data compilation',
                  'Tender analysis and reporting with a clear tender recommendation',
                  'Tender clarification management and addendum issue',
                  'Value engineering and scope optimisation to meet a target budget',
                  'Payment schedule drafting and cashflow planning',
                  'Pre start meeting pack and mobilisation checklist'
                ].map((item, index) => (
                  <li key={item || index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary-navy flex-shrink-0 mt-0.5" />
                    <span className="text-text-muted">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-border-light py-12">
        <div className="content-container">
          <div className="text-center space-y-4">
            <span className="text-lg font-semibold text-primary-navy">Schedule of Works</span>
            <p className="text-sm text-text-muted">Professional Schedules of Work for UK projects</p>
            <div className="flex justify-center space-x-6 pt-4">
              <button onClick={() => navigate('/')} className="text-text-muted hover:text-primary-navy transition-colors">
                Home
              </button>
              <button onClick={() => navigate('/faq')} className="text-text-muted hover:text-primary-navy transition-colors">
                FAQ
              </button>
              <button onClick={() => navigate('/upload')} className="text-text-muted hover:text-primary-navy transition-colors">
                Get Started
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};
